import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { SectionTitle, Card, SaveButton } from "../ui";

const FIELD = "bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50";

const ADDON_RATE_FIELDS = [
  ["tolls_and_tip", "Tolls & tip (add-on)"],
  ["driver_fee", "Hired driver"],
  ["hotel_per_night", "Hotel per night"],
  ["pet_fee_per_pet", "Pet fee (per pet)"],
];

function RangeRow({ label, val = { min: 0, max: 0 }, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex-1 text-sm text-charcoal-700">{label}</span>
      <input type="number" value={val.min} onChange={(e) => onChange({ ...val, min: Number(e.target.value) })} className={`w-28 ${FIELD}`} />
      <span className="text-charcoal-400">–</span>
      <input type="number" value={val.max} onChange={(e) => onChange({ ...val, max: Number(e.target.value) })} className={`w-28 ${FIELD}`} />
    </div>
  );
}

function ListEditor({ label, items = [], onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-charcoal-600">{label} <span className="font-normal text-charcoal-400">(one per line)</span></label>
      <textarea data-testid={testid} rows={5} value={items.join("\n")}
        onChange={(e) => onChange(e.target.value.split("\n"))}
        onBlur={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
        className={`mt-1 w-full ${FIELD}`} />
    </div>
  );
}

export default function CalculatorSettings() {
  const [cfg, setCfg] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/calculator-config").then((res) => setCfg(res.data));
    api.get("/admin/custom-requests").then((res) => setReqs(res.data)).catch(() => {});
    api.get("/admin/calculator-analytics").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  const save = async () => {
    const ranges = [["base_relocation", "Base Relocation price"], ...ADDON_RATE_FIELDS];
    for (const [key, label] of ranges) {
      const v = cfg[key];
      if (!v || v.min == null || v.max == null || v.min > v.max) {
        toast.error(`Check the "${label}" range — min must be ≤ max.`);
        return;
      }
    }
    setSaving(true);
    const { _id, ...payload } = cfg;
    if (payload.packages?.advice_only) payload.packages.advice_only.pricing_mode = "flat";
    const { data } = await api.put("/admin/calculator-config", payload);
    setCfg(data);
    toast.success("Pricing saved — live on the site now");
    setSaving(false);
  };

  const setPkg = (id, key, val) => setCfg({
    ...cfg,
    packages: { ...cfg.packages, [id]: { ...cfg.packages[id], [key]: val } },
  });
  const setBundle = (i, k, v) => {
    const list = [...(cfg.bundles || [])];
    if (k === "featured" && v) list.forEach((x, idx) => { if (idx !== i) list[idx] = { ...x, featured: false }; });
    list[i] = { ...list[i], [k]: v };
    setCfg({ ...cfg, bundles: list });
  };
  const toggleBundleAddon = (i, aid) => {
    const b = cfg.bundles[i];
    const ids = b.addon_ids?.includes(aid) ? b.addon_ids.filter((x) => x !== aid) : [...(b.addon_ids || []), aid];
    setBundle(i, "addon_ids", ids);
  };
  const addBundle = () => setCfg({
    ...cfg,
    bundles: [...(cfg.bundles || []), { id: `bundle_${Date.now()}`, name: "New bundle", description: "", addon_ids: [], price_override: null, visible: true }],
  });
  const delBundle = (i) => {
    if (!window.confirm(`Remove "${cfg.bundles[i].name}"?`)) return;
    setCfg({ ...cfg, bundles: cfg.bundles.filter((_, x) => x !== i) });
  };
  const bundleComputed = (b) => {
    if (b.price_override) return { min: Number(b.price_override), max: Number(b.price_override) };
    let lo = cfg.base_relocation?.min || 0, hi = cfg.base_relocation?.max || 0;
    for (const aid of b.addon_ids || []) {
      const s = cfg.addon_services.find((x) => x.id === aid);
      if (s) { lo += s.min || 0; hi += s.max || 0; }
    }
    return { min: lo, max: hi };
  };
  const setSvc = (i, k, v) => {
    const list = [...cfg.addon_services];
    list[i] = { ...list[i], [k]: v };
    setCfg({ ...cfg, addon_services: list });
  };
  const addSvc = () => setCfg({
    ...cfg,
    addon_services: [...cfg.addon_services, {
      id: `svc_${Date.now()}`, label: "New service", description: "", min: 0, max: 0,
      visible: true, per_unit: false, unit_label: "",
    }],
  });
  const delSvc = (i) => {
    if (!window.confirm(`Remove "${cfg.addon_services[i].label}"?`)) return;
    setCfg({ ...cfg, addon_services: cfg.addon_services.filter((_, x) => x !== i) });
  };
  const markReviewed = async (r) => {
    await api.patch(`/admin/custom-requests/${r.id}`, { status: "reviewed" });
    setReqs(reqs.map((x) => x.id === r.id ? { ...x, status: "reviewed" } : x));
  };

  if (!cfg) return <p className="text-charcoal-500">Loading…</p>;
  const base = cfg.packages?.base_relocation || {};
  const advice = cfg.packages?.advice_only || {};
  const newReqs = reqs.filter((r) => r.status === "new");

  return (
    <div data-testid="admin-calculator">
      <SectionTitle title="Pricing & packages" subtitle={`Every package, price, label, and rule below is live on the site the moment you save.${cfg.updated_at ? ` Last updated ${cfg.updated_at.slice(0, 10)}.` : ""}`} />
      <div className="space-y-6">

        {stats && stats.total_sessions > 0 && (
          <Card>
            <h3 className="font-medium text-charcoal-800 mb-1">Estimates configured by visitors</h3>
            <p className="text-xs text-charcoal-500 mb-4">One entry per visitor session — see which packages, directions, and add-ons people actually pick.</p>
            <div className="grid gap-4 sm:grid-cols-3" data-testid="calc-analytics">
              <div className="rounded-xl bg-sand-100 p-4">
                <p className="font-serif text-3xl text-charcoal-800" data-testid="calc-analytics-total">{stats.total_sessions}</p>
                <p className="text-xs text-charcoal-500 mt-1">estimate sessions</p>
              </div>
              <div className="rounded-xl bg-sand-100 p-4 text-sm space-y-1">
                <p className="text-xs font-medium uppercase tracking-wider text-charcoal-500 mb-1">Package split</p>
                <div className="flex justify-between"><span className="text-charcoal-600">Driving</span><span className="text-charcoal-800">{stats.by_package.base_relocation || 0}</span></div>
                <div className="flex justify-between"><span className="text-charcoal-600">Flying</span><span className="text-charcoal-800">{stats.by_package.advice_only || 0}</span></div>
              </div>
              <div className="rounded-xl bg-sand-100 p-4 text-sm space-y-1">
                <p className="text-xs font-medium uppercase tracking-wider text-charcoal-500 mb-1">Direction (Driving)</p>
                <div className="flex justify-between"><span className="text-charcoal-600">Border → City</span><span className="text-charcoal-800">{stats.by_direction.border_to_city || 0}</span></div>
                <div className="flex justify-between"><span className="text-charcoal-600">City → Border</span><span className="text-charcoal-800">{stats.by_direction.city_to_border || 0}</span></div>
              </div>
            </div>
            {stats.top_addons.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium uppercase tracking-wider text-charcoal-500 mb-2">Most-selected add-ons</p>
                <div className="flex flex-wrap gap-2">
                  {stats.top_addons.map(([label, n]) => (
                    <span key={label} className="rounded-full bg-sage/10 px-3 py-1 text-xs text-charcoal-700">{label} · {n}</span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* 1. Driving package */}
        <Card>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-medium text-charcoal-800">1 · Driving package (core service)</h3>
            <label className="flex items-center gap-1.5 text-xs text-charcoal-600">
              <input type="checkbox" data-testid="pkg-base-visible" checked={base.visible !== false} onChange={(e) => setPkg("base_relocation", "visible", e.target.checked)} /> visible on site
            </label>
          </div>
          <p className="text-xs text-charcoal-500 mb-4">Your primary service — driving the client's vehicle between the US border and their city, in either direction. Shown first and selected by default on the pricing page.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-charcoal-600">Package name</label>
              <input data-testid="pkg-base-name" value={base.name || ""} onChange={(e) => setPkg("base_relocation", "name", e.target.value)} className={`mt-1 w-full ${FIELD}`} />
            </div>
            <div>
              <label className="text-xs font-medium text-charcoal-600">Short description</label>
              <textarea data-testid="pkg-base-desc" rows={2} value={base.short_description || ""} onChange={(e) => setPkg("base_relocation", "short_description", e.target.value)} className={`mt-1 w-full ${FIELD}`} />
            </div>
            <RangeRow label="Starting price range (USD)" val={cfg.base_relocation} onChange={(v) => setCfg({ ...cfg, base_relocation: v })} />
            <div className="grid gap-4 md:grid-cols-2">
              <ListEditor label="What's included" items={base.included} onChange={(v) => setPkg("base_relocation", "included", v)} testid="pkg-base-included" />
              <ListEditor label="Not included" items={base.not_included} onChange={(v) => setPkg("base_relocation", "not_included", v)} testid="pkg-base-excluded" />
            </div>
          </div>
        </Card>

        {/* 2. Flying — flat fee */}
        <Card>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-medium text-charcoal-800">2 · Flying — flat fee</h3>
            <label className="flex items-center gap-1.5 text-xs text-charcoal-600">
              <input type="checkbox" data-testid="pkg-advice-visible" checked={advice.visible !== false} onChange={(e) => setPkg("advice_only", "visible", e.target.checked)} /> visible on site
            </label>
          </div>
          <p className="text-xs text-charcoal-500 mb-4">A simple side offer for clients flying instead of driving: one flat price, advice and help only. No add-ons or bundles ever attach to this package.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-charcoal-600">Package name</label>
              <input data-testid="pkg-advice-name" value={advice.name || ""} onChange={(e) => setPkg("advice_only", "name", e.target.value)} className={`mt-1 w-full ${FIELD}`} />
            </div>
            <div>
              <label className="text-xs font-medium text-charcoal-600">Short description</label>
              <textarea data-testid="pkg-advice-desc" rows={2} value={advice.short_description || ""} onChange={(e) => setPkg("advice_only", "short_description", e.target.value)} className={`mt-1 w-full ${FIELD}`} />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-charcoal-700">Pricing</span>
              <select data-testid="pkg-advice-mode" value={advice.pricing_mode || "flat"} onChange={(e) => setPkg("advice_only", "pricing_mode", e.target.value)} className={FIELD}>
                <option value="flat">Flat fee</option>
                <option value="range">Estimated range</option>
              </select>
              {(advice.pricing_mode || "flat") === "flat" ? (
                <>
                  <span className="text-sm text-charcoal-500">USD</span>
                  <input data-testid="pkg-advice-flat" type="number" value={advice.flat_price ?? 0} onChange={(e) => setPkg("advice_only", "flat_price", Number(e.target.value))} className={`w-28 ${FIELD}`} />
                </>
              ) : (
                <>
                  <input type="number" value={advice.min ?? 0} onChange={(e) => setPkg("advice_only", "min", Number(e.target.value))} className={`w-28 ${FIELD}`} />
                  <span className="text-charcoal-400">–</span>
                  <input type="number" value={advice.max ?? 0} onChange={(e) => setPkg("advice_only", "max", Number(e.target.value))} className={`w-28 ${FIELD}`} />
                </>
              )}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <ListEditor label="What's included" items={advice.included} onChange={(v) => setPkg("advice_only", "included", v)} testid="pkg-advice-included" />
              <ListEditor label="Not included" items={advice.not_included} onChange={(v) => setPkg("advice_only", "not_included", v)} testid="pkg-advice-excluded" />
            </div>
          </div>
        </Card>

        {/* 3. Suggested bundles */}
        <Card>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-medium text-charcoal-800">3 · Suggested bundles</h3>
            <button data-testid="bundle-add" onClick={addBundle} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-1.5 text-xs font-medium text-white hover:bg-terracotta-hover"><Plus size={13} /> Add bundle</button>
          </div>
          <p className="text-xs text-charcoal-500 mb-4">Preset Driving-package combinations shown on the pricing page (never attached to Flying). <strong>Bundle prices are computed live from your base + add-on rates — one source of truth, nothing to keep in sync.</strong> Set a price override only if you want a flat bundle price instead.</p>
          <div className="space-y-4">
            {(cfg.bundles || []).map((b, i) => {
              const price = bundleComputed(b);
              return (
                <div key={b.id} data-testid={`bundle-row-${b.id}`} className="rounded-xl border border-sand-200 p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <input value={b.name} onChange={(e) => setBundle(i, "name", e.target.value)} className={`flex-1 min-w-[160px] font-medium ${FIELD}`} data-testid={`bundle-name-${b.id}`} />
                    <span className="text-xs text-charcoal-500" data-testid={`bundle-computed-${b.id}`}>
                      {b.price_override ? "override:" : "auto:"} ${price.min.toLocaleString()}{price.min !== price.max && `–$${price.max.toLocaleString()}`}
                    </span>
                    <input type="number" placeholder="override $" value={b.price_override ?? ""} data-testid={`bundle-override-${b.id}`}
                      onChange={(e) => setBundle(i, "price_override", e.target.value === "" ? null : Number(e.target.value))} className={`w-28 ${FIELD}`} />
                    <label className="flex items-center gap-1.5 text-xs text-charcoal-600">
                      <input type="checkbox" checked={b.visible !== false} onChange={(e) => setBundle(i, "visible", e.target.checked)} /> visible
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-terracotta">
                      <input type="checkbox" data-testid={`bundle-featured-${b.id}`} checked={!!b.featured} onChange={(e) => setBundle(i, "featured", e.target.checked)} /> most popular
                    </label>
                    <button onClick={() => delBundle(i)} className="p-1 text-charcoal-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                  <input value={b.description || ""} onChange={(e) => setBundle(i, "description", e.target.value)}
                    placeholder="Short description shown to clients" className={`w-full text-xs ${FIELD}`} />
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {cfg.addon_services.map((s) => (
                      <label key={s.id} className="flex items-center gap-1.5 text-xs text-charcoal-600">
                        <input type="checkbox" checked={(b.addon_ids || []).includes(s.id)} onChange={() => toggleBundleAddon(i, s.id)} data-testid={`bundle-${b.id}-addon-${s.id}`} /> {s.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* 4. Add-on rates */}
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-1">4 · Optional add-on rates (USD)</h3>
          <p className="text-xs text-charcoal-500 mb-4">Priced only when a client selects them. Transport add-ons apply to the Base Relocation package only.</p>
          <div className="space-y-3">
            {ADDON_RATE_FIELDS.map(([key, label]) => (
              <RangeRow key={key} label={label} val={cfg[key]} onChange={(v) => setCfg({ ...cfg, [key]: v })} />
            ))}
          </div>
        </Card>

        {/* 5. Add-on services */}
        <Card>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-medium text-charcoal-800">5 · Optional add-on services</h3>
            <button data-testid="svc-add" onClick={addSvc} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-1.5 text-xs font-medium text-white hover:bg-terracotta-hover"><Plus size={13} /> Add service</button>
          </div>
          <p className="text-xs text-charcoal-500 mb-4">Shown on the public pricing page as checkboxes (for both packages). Hidden services stay saved but never display or price.</p>
          <div className="space-y-4">
            {cfg.addon_services.map((s, i) => (
              <div key={s.id} data-testid={`svc-row-${s.id}`} className="rounded-xl border border-sand-200 p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <input value={s.label} onChange={(e) => setSvc(i, "label", e.target.value)} className={`flex-1 min-w-[200px] font-medium ${FIELD}`} />
                  <input type="number" value={s.min} onChange={(e) => setSvc(i, "min", Number(e.target.value))} className={`w-24 ${FIELD}`} />
                  <span className="text-charcoal-400">–</span>
                  <input type="number" value={s.max} onChange={(e) => setSvc(i, "max", Number(e.target.value))} className={`w-24 ${FIELD}`} />
                  <label className="flex items-center gap-1.5 text-xs text-charcoal-600">
                    <input type="checkbox" checked={!!s.visible} onChange={(e) => setSvc(i, "visible", e.target.checked)} /> visible
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-charcoal-600">
                    <input type="checkbox" checked={!!s.per_unit} onChange={(e) => setSvc(i, "per_unit", e.target.checked)} /> quantity
                  </label>
                  {s.per_unit && (
                    <input value={s.unit_label || ""} onChange={(e) => setSvc(i, "unit_label", e.target.value)} placeholder="unit (e.g. visit)" className={`w-28 ${FIELD}`} />
                  )}
                  <button onClick={() => delSvc(i)} className="p-1 text-charcoal-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
                <input value={s.description || ""} onChange={(e) => setSvc(i, "description", e.target.value)}
                  placeholder="Short description shown to clients" className={`w-full text-xs ${FIELD}`} />
              </div>
            ))}
          </div>
        </Card>

        {/* 6. Pricing logic & disclaimer */}
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-2">6 · Pricing logic & disclaimer</h3>
          <div className="flex items-center gap-3 mb-4">
            <span className="flex-1 text-sm text-charcoal-700">Planning buffer (%) — applied to the whole estimate</span>
            <input data-testid="calc-buffer-pct" type="number" value={cfg.buffer_pct}
              onChange={(e) => setCfg({ ...cfg, buffer_pct: Number(e.target.value) })} className={`w-28 ${FIELD}`} />
          </div>
          <label className="text-xs font-medium text-charcoal-600">Estimate disclaimer (shown under every estimate)</label>
          <textarea data-testid="calc-disclaimer-input" rows={3} value={cfg.estimate_disclaimer || ""}
            onChange={(e) => setCfg({ ...cfg, estimate_disclaimer: e.target.value })} className={`mt-1 w-full ${FIELD}`} />
        </Card>

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-1">Custom pricing requests {newReqs.length > 0 && <span className="ml-1 rounded-full bg-terracotta px-2 py-0.5 text-xs text-white">{newReqs.length} new</span>}</h3>
          <p className="text-xs text-charcoal-500 mb-3">Visitor requests that need manual pricing — include them in the consultation quote.</p>
          {reqs.length === 0 ? <p className="text-sm text-charcoal-500">No custom requests yet.</p> : (
            <div className="divide-y divide-sand-100">
              {reqs.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-3 py-3" data-testid={`custom-req-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-charcoal-800">{r.text}</p>
                    <p className="text-xs text-charcoal-500 mt-0.5">{r.email || "no email"} · {r.created_at?.slice(0, 10)}</p>
                  </div>
                  {r.status === "new" ? (
                    <button onClick={() => markReviewed(r)} data-testid={`custom-req-review-${r.id}`}
                      className="shrink-0 rounded-full bg-sage px-3 py-1 text-xs font-medium text-white hover:bg-sage-hover">Mark reviewed</button>
                  ) : (
                    <span className="shrink-0 rounded-full bg-sand-200 px-3 py-1 text-xs text-charcoal-500">reviewed</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <SaveButton testid="calc-save" onClick={save} saving={saving} />
      </div>
    </div>
  );
}
